/**
 * Hook personnalisé pour récupérer et gérer les données Odoo
 *
 * Ce hook gère :
 * - Le chargement des données depuis l'API Odoo
 * - Le traitement des données avec dataProcessor
 * - La gestion des états (loading, error)
 * - Le rafraîchissement manuel des données
 *
 * @param {number} projectId - ID du projet Odoo
 * @param {number} partnerId - ID du partenaire/client
 * @returns {Object} { userStories, epics, milestones, loading, error, refresh }
 */

import { useState, useEffect, useCallback } from 'react';
import backendApi from '../services/backendApi';

export const useOdooData = (projectId, partnerId) => {
  const [data, setData] = useState({
    isAdmin: false,
    userStories: [],
    epics: [],
    milestones: [],
    tickets: [],
    helpdeskStages: [],
    taskStages: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fonction pour charger les données depuis Odoo via le backend
   */
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      console.log('🔍 useOdooData: Récupération projet', projectId);

      const projectData = await backendApi.getProjectDetails(projectId);

      console.log('✅ useOdooData: Données reçues', projectData);

      // Les données arrivent déjà formatées et filtrées du backend
      const { tasks = [], milestones = [], epics = [], tickets = [], helpdeskStages = [], taskStages = [] } = projectData;

      // Les tasks sont déjà filtrées côté backend selon les milestones du client
      // Pas besoin de filtrage supplémentaire ici

      // Construire un map taskId → milestoneName pour enrichir les user stories
      const taskMilestoneMap = {};
      milestones.forEach(m => {
        if (m.taskIds && Array.isArray(m.taskIds)) {
          m.taskIds.forEach(taskId => {
            taskMilestoneMap[taskId] = { milestoneId: m.id, milestoneName: m.name };
          });
        }
      });

      // Transformer les tasks en user stories avec catégories basées sur le statut
      const userStories = tasks.map(task => {
        let category = 'Backlog';

        // Déterminer la catégorie selon le nom du stage
        const stageName = task.stageName?.toLowerCase() || '';
        if (task.stageId === 397 || stageName.includes('refus')) {
          category = 'Refusé';
        } else if (stageName.includes('done')) {
          category = 'Done';
        } else if (stageName.includes('test') && stageName.includes('client')) {
          category = 'Tests à valider';
        } else if (stageName.includes('test')) {
          category = 'En Test';
        } else if (stageName.includes('en cours')) {
          category = 'En cours';
        } else if (stageName.includes('backlog')) {
          category = 'Backlog';
        } else if (stageName.includes('confirmation') || stageName.includes('confirm')) {
          category = 'Confirmation';
        } else if (stageName.includes('raffinage') || stageName.includes('po review') || stageName.includes('review')) {
          category = 'Raffinage';
        }

        const milestoneInfo = taskMilestoneMap[task.id] || {};

        return {
          id: task.id,
          canDecide: !!task.canDecide,
          ...(projectData.isAdmin ? {
            isBilled: !!task.isBilled,
            saleOrderName: task.saleOrderName || null,
          } : {}),
          clientValidated: !!task.clientValidated,
          hasAcceptanceCriteria: !!task.hasAcceptanceCriteria,
          acceptanceCriteriaValidated: task.acceptanceCriteriaValidated || 0,
          acceptanceCriteriaTotal: task.acceptanceCriteriaTotal || 0,
          creatorId: task.creatorId,
          creatorName: task.creatorName,
          name: task.name,
          code: task.code,
          description: task.description || task.name,
          category,
          epicId: task.epicId,
          epicName: task.epicName,
          milestoneId: milestoneInfo.milestoneId || null,
          milestoneName: milestoneInfo.milestoneName || null,
          stageId: task.stageId,
          stageName: category === 'Done' ? 'Done' : task.stageName,
          deadline: task.deadline,
          priority: task.priority,
          assignees: task.assignees || [],
          storyPoints: task.storyPoints || 0,
          hourlyRate: (task.storyPoints || 0) * 0.5,
          dateTestClient: task.dateTestClient || null,
          dateStageEntered: task.dateStageEntered || null,
          lastClientFeedbackAt: task.lastClientFeedbackAt || null,
          clientFeedbackAvailable: task.clientFeedbackAvailable === true,
          dateDone: task.dateDone || null,
          dateBacklog: task.dateBacklog || null
        };
      });

      // Calculer la progression par Epic
      const epicProgress = epics.map(epic => {
        const epicTasks = tasks.filter(t => t.epicId === epic.id);
        const totalTasks = epicTasks.length;
        const doneTasks = epicTasks.filter(t => {
          const stageName = t.stageName?.toLowerCase() || '';
          return stageName.includes('done') || stageName.includes('terminé');
        }).length;

        return {
          id: epic.id,
          name: epic.name,
          total: totalTasks,
          done: doneTasks,
          progress: totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0
        };
      });

      // Traiter les jalons avec détermination du statut et calcul du pourcentage de complétion
      const now = new Date();
      const processedMilestones = milestones.map(m => {
        let status = 'upcoming';

        if (m.isReached) {
          status = 'completed';
        } else if (m.deadline) {
          const deadline = new Date(m.deadline);
          const diffDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));

          if (diffDays < 0) {
            status = 'overdue';
          } else if (diffDays <= 7) {
            status = 'inprogress';
          }
        }

        // Calculer le pourcentage de complétion basé sur les tâches du milestone
        let completionPercentage = 0;
        let totalTasks = 0;
        let doneTasks = 0;

        if (m.taskIds && m.taskIds.length > 0) {
          // Filtrer les tâches qui appartiennent à ce milestone
          const milestoneTasks = tasks.filter(t => m.taskIds.includes(t.id));
          totalTasks = milestoneTasks.length;
          doneTasks = milestoneTasks.filter(t => {
            const stageName = t.stageName?.toLowerCase() || '';
            return stageName.includes('done') || stageName.includes('terminé');
          }).length;

          completionPercentage = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
        }

        return {
          id: m.id,
          name: m.name,
          date: m.deadline,
          deadline: m.deadline,
          isReached: m.isReached,
          status,
          completionPercentage,
          totalTasks,
          doneTasks,
          taskIds: m.taskIds || []
        };
      });

      setData({
        isAdmin: !!projectData.isAdmin,
        userStories,
        epics: epicProgress,
        milestones: processedMilestones,
        tickets,
        helpdeskStages,
        taskStages,
      });
    } catch (err) {
      console.error('❌ useOdooData: Erreur complète:', err);
      console.error('❌ useOdooData: Message:', err.message);
      console.error('❌ useOdooData: Stack:', err.stack);
      if (silent) throw err;
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId]);

  /**
   * Fonction pour rafraîchir manuellement les données
   */
  const refresh = useCallback(async (options = {}) => {
    await backendApi.flushCache();
    return fetchData(options);
  }, [fetchData]);

  const upsertTicket = useCallback((ticket) => {
    if (!ticket?.id) return;
    setData(current => ({
      ...current,
      tickets: [ticket, ...current.tickets.filter(item => item.id !== ticket.id)],
    }));
  }, []);

  const updateTicketStage = useCallback((ticketId, stageId, stageName) => {
    setData(current => ({
      ...current,
      tickets: current.tickets.map(ticket => ticket.id === ticketId
        ? { ...ticket, stageId, stageName }
        : ticket),
    }));
  }, []);

  // Charger les données au montage et lors du changement de projectId
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh toutes les 5 minutes pour refléter les changements Odoo
  useEffect(() => {
    if (!projectId) return;
    const interval = setInterval(() => {
      // Garder les formulaires ouverts pendant la synchronisation périodique.
      // fetchData journalise déjà les erreurs; éviter un rejet non géré ici.
      fetchData({ silent: true }).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [projectId, fetchData]);

  return {
    isAdmin: data.isAdmin,
    userStories: data.userStories,
    epics: data.epics,
    milestones: data.milestones,
    tickets: data.tickets,
    helpdeskStages: data.helpdeskStages,
    taskStages: data.taskStages,
    loading,
    error,
    refresh,
    upsertTicket,
    updateTicketStage,
  };
};

export default useOdooData;
